import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import SignUpDialog from "@/components/SignUpDialog";
import { useAuth } from "@/hooks/useAuth";
import heroImg from "@/assets/hero-community-hands.jpg";
import { FEATURED_IDEA_ID, FEATURED_IDEA_TITLE, FEATURED_LOCATION } from "@/lib/featured";

const PrichardCaseStudyHero = () => {
  const [signUpOpen, setSignUpOpen] = useState(false);
  const { user } = useAuth();

  const handlePrimary = () => {
    if (!user) setSignUpOpen(true);
  };

  return (
    <>
      <SignUpDialog open={signUpOpen} onOpenChange={setSignUpOpen} />
      <section className="relative bg-hero overflow-hidden pt-20">
        <div className="absolute inset-y-0 right-0 w-full md:w-3/5 lg:w-1/2">
          <img
            src={heroImg}
            alt="Two pairs of hands clasped together over an Alabama map"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(140_12%_14%)] via-[hsl(140_12%_14%)]/70 to-transparent md:via-[hsl(140_12%_14%)]/40 md:from-[hsl(140_12%_14%)]/95" />
        </div>

        <div className="container mx-auto px-6 pt-12 md:pt-20 pb-16 md:pb-24 relative z-10">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide border border-white/15 bg-white/5 backdrop-blur text-hero-fg uppercase"
            >
              <Sparkles className="h-3 w-3" />
              <span>Featured Case Study</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="flex items-center gap-2 mb-5 text-hero-muted text-xs font-semibold tracking-[0.2em] uppercase"
            >
              <MapPin className="h-3.5 w-3.5" />
              <span>{FEATURED_LOCATION}</span>
              <span className="w-1 h-1 rounded-full bg-current opacity-60" />
              <span>Economic Development</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display text-[2.25rem] sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-hero-fg mb-5 md:mb-6"
            >
              Every block grant, every brief, every vote — built around <span className="text-accent">one idea</span> at a time.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-hero-muted text-sm md:text-lg max-w-xl mb-8 leading-relaxed"
            >
              Follow how a single idea — revitalizing {FEATURED_IDEA_TITLE.replace("Revitalize ", "")} — moves through canvassing, planning, funding, and growing on this platform. Then bring yours.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap items-center gap-3"
            >
              {user ? (
                <Button
                  asChild
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90 font-display font-semibold text-base px-7 h-12 rounded-full shadow-glow"
                >
                  <Link to="/ideas?new=1">
                    Start your idea <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              ) : (
                <Button
                  size="lg"
                  onClick={handlePrimary}
                  className="bg-accent text-accent-foreground hover:bg-accent/90 font-display font-semibold text-base px-7 h-12 rounded-full shadow-glow"
                >
                  Start your idea <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/25 bg-white/5 text-hero-fg hover:bg-white/10 hover:text-hero-fg font-display font-semibold text-base px-6 h-12 rounded-full"
              >
                <Link to={`/ideas/${FEATURED_IDEA_ID}`}>Follow this idea</Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrichardCaseStudyHero;